import './BusinessImpactOverview.scss';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import {
  IncrementalRoasTakeaways,
  ModelOverview
} from '@blisspointmedia/bpm-types/dist/BrandEquity';
import { DateGroupingKey } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import {
  capitalizeWords,
  currencyFormatter,
  numberFormatter
} from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { ChartContainer } from '@Components/ChartContainer/ChartContainer';
import { AreaChart } from '@Components/Charts/AreaChart/AreaChart';
import { headerFormatter } from '@Components/Charts/ChartUtils';
import { LineChart } from '@Components/Charts/LineChart/LineChart';
import { Placement, Size } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown/DownloadDropdown';
import { Dropdown, DropdownToggleType } from '@Components/Dropdown';
import { DynamicModelOverview } from '@Components/DynamicModelOverview/DynamicModelOverview';
import { InfoTooltip } from '@Components/InfoTooltip/InfoTooltip';
import { WidgetContainer } from '@Components/WidgetContainer/WidgetContainer';
import { Channel1, Channel2, Channel3, Channel4 } from '@src/utils/colors';
import { dateToMddyy } from '@src/utils/date-utils';
import { downloadPNG, exportToExcel } from '@src/utils/download-utils';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import {
  DATE_GROUPING_OPTIONS_BRAND_EQUITY,
  formatModelOverviewOutcomeVariable
} from '../BrandEquityUtils';
import { buildModelOverviewSections } from '../buildModelOverviewSections';
import { useGroupedByDate } from '../useGroupedByDate';

interface WeeklyRevenueTimeSeries {
  date: string;
  revenue: number;
}

interface BusinessImpactOverviewProps {
  weeklyBrandHealthTimeSeries: any[];
  weeklyRevenueTimeSeries: WeeklyRevenueTimeSeries[];
  modelOverviewAll: ModelOverview;
  incrementalRoasTakeaways: IncrementalRoasTakeaways;
  isQv: boolean;
  queryVolumeLongLabel: string;
  queryVolumeShortLabel: string;
}

const colors = [Channel1, Channel3, Channel2, Channel4];

export const BusinessImpactOverview = ({
  weeklyBrandHealthTimeSeries,
  weeklyRevenueTimeSeries,
  modelOverviewAll: modelOverview,
  incrementalRoasTakeaways,
  isQv,
  queryVolumeLongLabel,
  queryVolumeShortLabel
}: BusinessImpactOverviewProps): ReactElement => {
  const [weeklyRevenueChartDateGrouping, setWeeklyRevenueChartDateGrouping] =
    useState<DateGroupingKey>('Week');
  const [weeklyBrandHealthChartDateGrouping, setWeeklyBrandHealthChartDateGrouping] =
    useState<DateGroupingKey>('Week');
  const { fiscal_cal: fiscalCal } = useCompanyInfo();
  const weeklyBrandHealthTimeSeriesByDate = useGroupedByDate(
    weeklyBrandHealthTimeSeries,
    weeklyBrandHealthChartDateGrouping
  );

  const finalWeeklyBrandHealthAreaChartData = useGroupedByDate(
    weeklyRevenueTimeSeries,
    weeklyRevenueChartDateGrouping
  );

  const keysWithoutDate = useMemo(
    () =>
      Object.keys(weeklyBrandHealthTimeSeries[0])
        .filter(key => key !== 'date')
        .sort(),
    [weeklyBrandHealthTimeSeries]
  );

  const excelDownloadWeeklyBrandHealth = useCallback(() => {
    exportToExcel(weeklyBrandHealthTimeSeriesByDate, 'weekly_brand_health');
  }, [weeklyBrandHealthTimeSeriesByDate]);

  const excelDownloadWeeklySpend = useCallback(() => {
    exportToExcel(weeklyRevenueTimeSeries, 'weekly_spend');
  }, [weeklyRevenueTimeSeries]);

  const excelDownloadModelOverview = useCallback(() => {
    exportToExcel([modelOverview], 'model_overview');
  }, [modelOverview]);

  const pngDownloadWeeklyBrandHealth = useCallback(async () => {
    await downloadPNG(
      '.business-impact-overviewleft > .chart-container:nth-child(2',
      'weekly_brand_health'
    );
  }, []);

  const pngDownloadWeeklyRevenue = useCallback(async () => {
    await downloadPNG('.business-impact-overviewleft .chart-container', 'weekly_revenue');
  }, []);

  const pngDownloadModelOverview = useCallback(async () => {
    await downloadPNG('.business-impact-overview .brand-equity-right', 'model_overview');
  }, []);

  return (
    <WidgetContainer
      collapsible
      header='Overview'
      subHeader={
        <>
          Assessing the impact of brand equity on Revenue outcomes.
          <InfoTooltip rightLabel='More info' size={Size.SM}>
            The goal is to quantify the independent effect of brand equity on revenue. We employ a
            multivariate time series model that regresses revenue on brand equity metrics while
            controlling for impacts of paid media and other background factors. In the estimation,
            the model accounts for non-linearities in effects and the often significant timing
            differences between improvement in brand equity and its eventual impact on revenue.
          </InfoTooltip>
        </>
      }
    >
      {Object.keys(modelOverview).length !== 0 &&
        weeklyBrandHealthTimeSeries.length !== 0 &&
        weeklyRevenueTimeSeries.length !== 0 && (
          <div className='business-impact-overview'>
            <div className='business-impact-overviewleft'>
              <ChartContainer
                rightActions={
                  <DownloadDropdown
                    onClickOptions={[excelDownloadWeeklySpend, pngDownloadWeeklyRevenue]}
                  />
                }
                title={
                  <>
                    <Dropdown
                      overlayPlacement={Placement.RIGHT}
                      size={Size.LG}
                      type={DropdownToggleType.WIDGET_TITLE}
                      value={weeklyRevenueChartDateGrouping}
                      options={DATE_GROUPING_OPTIONS_BRAND_EQUITY}
                      onChange={option => setWeeklyRevenueChartDateGrouping(option)}
                    />
                    <div>Revenue</div>
                  </>
                }
                titleAfterDashText={`${dateToMddyy(
                  weeklyRevenueTimeSeries[0].date
                )} – ${dateToMddyy(
                  weeklyRevenueTimeSeries[weeklyRevenueTimeSeries.length - 1].date
                )}`}
              >
                <AreaChart
                  data={finalWeeklyBrandHealthAreaChartData}
                  xAxisDataKey='date'
                  xAxisTickFormatter={val => dateToMddyy(val)}
                  dateGrouping={weeklyRevenueChartDateGrouping}
                  yAxisStaticWidth={75}
                  yAxisTickFormatter={val => currencyFormatter.format(val, 0)}
                  tooltipFormatter={val => {
                    if (!val) {
                      return val;
                    }
                    return val < 1000
                      ? currencyFormatter.format(val, 0)
                      : currencyFormatter.format(val, 1);
                  }}
                  areas={[
                    {
                      name: 'Revenue',
                      dataKey: 'revenue'
                    }
                  ]}
                  hardCodedColorName='Revenue'
                  hardCodedColor={Channel4}
                />
              </ChartContainer>
              <ChartContainer
                rightActions={
                  <DownloadDropdown
                    onClickOptions={[excelDownloadWeeklyBrandHealth, pngDownloadWeeklyBrandHealth]}
                  />
                }
                title={
                  <>
                    <Dropdown
                      overlayPlacement={Placement.RIGHT}
                      size={Size.LG}
                      type={DropdownToggleType.WIDGET_TITLE}
                      value={weeklyBrandHealthChartDateGrouping}
                      options={DATE_GROUPING_OPTIONS_BRAND_EQUITY}
                      onChange={option => setWeeklyBrandHealthChartDateGrouping(option)}
                    />
                    <div>Brand Health</div>
                  </>
                }
                titleAfterDashText={`${dateToMddyy(
                  weeklyBrandHealthTimeSeries[0].date
                )} – ${dateToMddyy(
                  weeklyBrandHealthTimeSeries[weeklyBrandHealthTimeSeries.length - 1].date
                )}
                `}
                beforeTooltipText={isQv ? 'Search Intent' : 'Positive Response'}
                tooltipText={
                  isQv
                    ? `${queryVolumeLongLabel} (${queryVolumeShortLabel}) measures changes in the volume of branded search intent over time. We use ${queryVolumeShortLabel} to proxy for brand equity as it represents a funnel step between awareness and an actual site visit or install.`
                    : 'Positive response indicates the percentage of survey respondents expressing favorable perceptions or experiences related to each brand health metric.'
                }
              >
                <LineChart
                  customTooltipHeader={val =>
                    headerFormatter(weeklyBrandHealthChartDateGrouping, val, fiscalCal)
                  }
                  data={weeklyBrandHealthTimeSeriesByDate}
                  lineDataKeys={keysWithoutDate.map(key => ({
                    name: isQv
                      ? queryVolumeShortLabel
                      : String(key).charAt(0).toUpperCase() + String(key).slice(1),
                    dataKey: key,
                    toolTipText: isQv
                      ? `${queryVolumeLongLabel} (${queryVolumeShortLabel}) measures changes in the volume of branded search intent over time. We use ${queryVolumeShortLabel} to proxy for brand equity as it represents a funnel step between awareness and an actual site visit or install.`
                      : 'ToolTipText'
                  }))}
                  colorMappings={keysWithoutDate.map((key, index) => ({
                    name: isQv
                      ? queryVolumeShortLabel
                      : String(key).charAt(0).toUpperCase() + String(key).slice(1),
                    color: colors[index]
                  }))}
                  tooltipLabelFormatter={val =>
                    isQv ? queryVolumeShortLabel : capitalizeWords(val)
                  }
                  tooltipFormatter={val =>
                    isQv ? val.toFixed(2) : `${numberFormatter.format(val, 0)}%`
                  }
                  tooltipShape='line'
                  usePercentageYAxis={!isQv}
                  useLeftYAxis={isQv}
                  xAxisDataKey='date'
                  xAxisTickFormatter={val => dateToMddyy(val)}
                />
              </ChartContainer>
            </div>
            <div className='brand-equity-right'>
              <DynamicModelOverview
                sections={buildModelOverviewSections(
                  modelOverview,
                  incrementalRoasTakeaways,
                  formatModelOverviewOutcomeVariable(modelOverview.kpi),
                  modelOverview.refresh_date
                )}
                rightActions={
                  <DownloadDropdown
                    onClickOptions={[excelDownloadModelOverview, pngDownloadModelOverview]}
                    menuOptions={['XLSX', 'PNG']}
                  />
                }
              />
            </div>
          </div>
        )}
    </WidgetContainer>
  );
};
